import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Get API URL from environment variable or use default
const API_URL = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// fetchBrands thunk — accepts optional { limit, offset, append } for paginated loads.
//   - With no args: fetches ALL brands (legacy behaviour)
//   - With { limit, offset }: fetches one page; reducer replaces list
//   - With { limit, offset, append: true }: fetches page; reducer appends to existing list
export const fetchBrands = createAsyncThunk(
  'brands/fetchBrands',
  async (args = {}, { rejectWithValue }) => {
    const { limit, offset, append = false } = args || {};
    const paginated = Number.isFinite(limit) && Number.isFinite(offset);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 480000);

      const url = paginated
        ? `${API_URL}/api/brands/dashboard?limit=${limit}&offset=${offset}`
        : `${API_URL}/api/brands/dashboard`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        mode: 'cors',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      let data;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        throw new Error(`Server returned non-JSON response: ${response.status} ${response.statusText}`);
      }

      if (!response.ok) {
        throw new Error(data?.message || `HTTP error! status: ${response.status}`);
      }

      if (data && data.success) {
        // Return shape varies: with pagination we also have total + offset + append flag
        return paginated
          ? { brands: data.data || [], total: data.total ?? null, offset: data.offset ?? offset, append }
          : data.data || [];
      } else {
        throw new Error(data?.message || 'API returned unsuccessful response');
      }
      
    } catch (err) {
      let errorMessage = 'An error occurred while fetching brands';
      
      if (err.name === 'AbortError') {
        errorMessage = 'Dashboard request timed out after 8 minutes. The system may be processing large amounts of data. Please try again.';
      } else if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

// Updated createBrand thunk with better success handling
export const createBrand = createAsyncThunk(
  'brands/createBrand',
  async (brandData, { rejectWithValue, dispatch }) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 300000);
      
      const response = await fetch(`${API_URL}/api/addbrand/checkBrandForUser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        mode: 'cors',
        signal: controller.signal,
        body: JSON.stringify(brandData)
      });
      
      clearTimeout(timeoutId);
      
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      if (data.success) {
        // Check if this is a "found in DB" case (no processing needed)
        if (data.skipProcessing || data.showProcessingPopup === false) {
          // Brand already exists, no polling needed
          return {
            ...data.data,
            brand: data.brand,
            brandUrl: data.data?.brandUrl || brandData.brandUrl,
            brandName: data.data?.brandName || brandData.brandName,
            showProcessingPopup: false,
            skipProcessing: true
          };
        } else {
          // New brand, start polling
          dispatch(startPolling({ 
            brandUrl: brandData.brandUrl,
            brandName: brandData.brandName 
          }));
          
          return {
            ...data.data,
            brandUrl: brandData.brandUrl,
            brandName: brandData.brandName,
            showProcessingPopup: true
          };
        }
      } else {
        throw new Error(data.message || 'Failed to process brand');
      }

    } catch (error) {
      let errorMessage = 'Network error. Please check your connection and try again.';
      
      if (error.name === 'AbortError') {
        errorMessage = 'Brand processing timed out after 5 minutes. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

// Polling thunk for processing status - NO userId PARAMETER NEEDED
export const pollProcessingStatus = createAsyncThunk(
  'brands/pollProcessingStatus',
  async ({ brandUrl }, { rejectWithValue }) => {
    try {
      // userId will be fetched from session on backend
      const response = await fetch(`${API_URL}/api/addbrand/polling/${brandUrl}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include', // This sends the session cookie
        mode: 'cors'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        brandUrl,
        processing_status: data.processing_status
      };

    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch processing status');
    }
  }
);

const initialState = {
  brands: [],
  loading: false,        // initial load / replace
  loadingMore: false,    // appending the next page
  total: null,           // total brand count when paginating; null = unknown
  error: null,
  createLoading: false,
  createError: null,
  createSuccess: false,
  loadingMessage: '',
  lastFetchTime: null,
  // Processing status tracking
  processingBrands: {}, // { brandUrl: { status: '1', step: 'Fetching backlinks...', brandName: 'Example' } }
  isPolling: false,
  // Show processing popup flag
  showProcessingPopup: false,
  currentProcessingBrand: null,
  // Active polling intervals
  pollingIntervals: {} // Store interval IDs for cleanup
};

const brandsSlice = createSlice({
  name: 'brands',
  initialState,
  reducers: {
    clearCreateStatus: (state) => {
      state.createError = null;
      state.createSuccess = false;
      state.showProcessingPopup = false;
      state.currentProcessingBrand = null;
    },
    setLoadingMessage: (state, action) => {
      state.loadingMessage = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Start polling for a brand
    startPolling: (state, action) => {
      const { brandUrl, brandName } = action.payload;
      
      state.processingBrands[brandUrl] = {
        status: '1',
        step: 'Initializing brand analysis...',
        isActive: true,
        brandName: brandName || brandUrl,
        startTime: new Date().toISOString()
      };
      state.isPolling = true;
      state.showProcessingPopup = true;
      state.currentProcessingBrand = brandUrl;
    },
    // Stop polling for a brand
    stopPolling: (state, action) => {
      const { brandUrl } = action.payload;
      
      if (state.processingBrands[brandUrl]) {
        state.processingBrands[brandUrl].isActive = false;
        state.processingBrands[brandUrl].endTime = new Date().toISOString();
      }
      
      // Check if any brands are still being polled
      const hasActivePolling = Object.values(state.processingBrands).some(brand => brand.isActive);
      state.isPolling = hasActivePolling;
      
      // Hide popup if no active polling
      if (!hasActivePolling) {
        state.showProcessingPopup = false;
        state.currentProcessingBrand = null;
      }
    },
    // Update processing step message
    updateProcessingStep: (state, action) => {
      const { brandUrl, step } = action.payload;
      if (state.processingBrands[brandUrl]) {
        state.processingBrands[brandUrl].step = step;
      }
    },
    // Hide processing popup manually
    hideProcessingPopup: (state) => {
      state.showProcessingPopup = false;
      state.currentProcessingBrand = null;
    },
    // Show processing popup manually
    showProcessingPopupAction: (state, action) => {
      const { brandUrl } = action.payload;
      state.showProcessingPopup = true;
      state.currentProcessingBrand = brandUrl;
    },
    // Clean up completed processing brands
    cleanupProcessingBrands: (state) => {
      const cutoffTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(); // 24 hours ago
      
      Object.keys(state.processingBrands).forEach(brandUrl => {
        const brand = state.processingBrands[brandUrl];
        if (!brand.isActive && brand.endTime && brand.endTime < cutoffTime) {
          delete state.processingBrands[brandUrl];
        }
      });
    },
    // Set polling interval ID for cleanup
    setPollingInterval: (state, action) => {
      const { brandUrl, intervalId } = action.payload;
      state.pollingIntervals[brandUrl] = intervalId;
    },
    // Clear polling interval
    clearPollingInterval: (state, action) => {
      const { brandUrl } = action.payload;
      if (state.pollingIntervals[brandUrl]) {
        delete state.pollingIntervals[brandUrl];
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchBrands — supports both full-list and paginated (append/replace) modes.
      .addCase(fetchBrands.pending, (state, action) => {
        const append = action.meta?.arg?.append === true;
        if (append) {
          state.loadingMore = true;
        } else {
          state.loading = true;
          state.loadingMessage = 'Loading dashboard data...';
        }
        state.error = null;
      })
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = null;
        state.loadingMessage = '';
        state.lastFetchTime = new Date().toISOString();

        const payload = action.payload;
        if (Array.isArray(payload)) {
          // Legacy non-paginated response — replace list, total unknown
          state.brands = payload;
          state.total = payload.length;
        } else if (payload && Array.isArray(payload.brands)) {
          // Paginated response
          state.total = payload.total ?? state.total;
          if (payload.append) {
            // Dedupe by id when appending so a refresh of an overlapping page
            // doesn't show two copies of the same card.
            const existingIds = new Set(state.brands.map(b => b.id));
            for (const b of payload.brands) {
              if (!existingIds.has(b.id)) state.brands.push(b);
            }
          } else {
            state.brands = payload.brands;
          }
        }
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload;
        state.loadingMessage = '';
        // Don't clear brands on a failed "load more" — user keeps what they have
        if (action.meta?.arg?.append !== true) {
          state.brands = [];
        }
      })
      // Updated create brand cases
      .addCase(createBrand.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
        state.createSuccess = false;
        state.showProcessingPopup = false;
      })
      .addCase(createBrand.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        state.createError = null;
        
        // Check if this is a "found in DB" case
        if (action.payload?.skipProcessing) {
          // Brand found in DB, add to list but don't show processing
          if (action.payload?.brand) {
            // Check if brand already exists in the list to avoid duplicates
            const existingIndex = state.brands.findIndex(
              b => b.id === action.payload.brand.id || 
                   (b.brand_url === action.payload.brand.brand_url && 
                    b.user_id === action.payload.brand.user_id)
            );
            
            if (existingIndex === -1) {
              state.brands.push(action.payload.brand);
            } else {
              // Update existing brand
              state.brands[existingIndex] = action.payload.brand;
            }
          }
          state.showProcessingPopup = false;
          state.currentProcessingBrand = null;
        } else {
          // New brand processing case
          if (action.payload?.brand) {
            state.brands.push(action.payload.brand);
          } else if (action.payload && !action.payload.showProcessingPopup) {
            state.brands.push(action.payload);
          }
          
          // Show processing popup if indicated
          if (action.payload?.showProcessingPopup) {
            state.showProcessingPopup = true;
            state.currentProcessingBrand = action.payload.brandUrl;
          }
        }
      })
      .addCase(createBrand.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
        state.createSuccess = false;
        state.showProcessingPopup = false;
      })
      // Polling processing status cases - UPDATED FOR 8 STEPS
      .addCase(pollProcessingStatus.fulfilled, (state, action) => {
        const { brandUrl, processing_status } = action.payload;
        
        if (state.processingBrands[brandUrl]) {
          state.processingBrands[brandUrl].status = processing_status;
          
          // Update step message based on status - NOW 8 STEPS + COMPLETED
          const stepMessages = {
            '1': 'Initializing brand analysis...',
            '2': 'Fetching backlinks...',
            '3': 'Performing Google search analysis...',
            '4': 'Classifying affiliate URLs...',
            '5': 'Processing backlink data...',
            '6': 'Analyzing organic keywords...',
            '7': 'Processing paid keywords...',
            '8': 'Finalizing geographic data...',
            'completed': 'Analysis completed successfully!',
            'failed': 'Processing failed. Please try again.'
          };
          
          state.processingBrands[brandUrl].step = stepMessages[processing_status] || `Processing step ${processing_status}...`;
          
          // Stop polling if completed or failed
          if (processing_status === 'completed' || processing_status === 'failed' || parseInt(processing_status) >= 8) {
            state.processingBrands[brandUrl].isActive = false;
            state.processingBrands[brandUrl].endTime = new Date().toISOString();
            
            // Check if any brands are still being polled
            const hasActivePolling = Object.values(state.processingBrands).some(brand => brand.isActive);
            state.isPolling = hasActivePolling;
            
            // Hide popup if no active polling
            if (!hasActivePolling) {
              state.showProcessingPopup = false;
              state.currentProcessingBrand = null;
            }
          }
        }
      })
      .addCase(pollProcessingStatus.rejected, (state, action) => {
        // Log polling errors but don't stop polling completely
        console.error('Polling error:', action.payload);
        
        // If error persists for a brand, you might want to mark it as failed
        // This is optional - you can handle this based on your requirements
      });
  }
});

// Action creators
export const { 
  clearCreateStatus, 
  setLoadingMessage, 
  clearError,
  startPolling,
  stopPolling,
  updateProcessingStep,
  hideProcessingPopup,
  showProcessingPopupAction,
  cleanupProcessingBrands,
  setPollingInterval,
  clearPollingInterval
} = brandsSlice.actions;

// Thunk for managing polling lifecycle
export const startPollingWithCleanup = (brandData) => (dispatch, getState) => {
  const { brandUrl, brandName } = brandData;
  
  // Start polling in state
  dispatch(startPolling({ brandUrl, brandName }));
  
  // Set up polling interval
  const intervalId = setInterval(() => {
    const state = getState();
    const processingInfo = state.brands.processingBrands[brandUrl];
    
    // Only poll if still active
    if (processingInfo?.isActive) {
      dispatch(pollProcessingStatus({ brandUrl }));
    } else {
      // Clean up interval if no longer active
      clearInterval(intervalId);
      dispatch(clearPollingInterval({ brandUrl }));
    }
  }, 3000); // Poll every 3 seconds
  
  // Store interval ID for cleanup
  dispatch(setPollingInterval({ brandUrl, intervalId }));
  
  return intervalId;
};

// Thunk for stopping polling with cleanup
export const stopPollingWithCleanup = (brandUrl) => (dispatch, getState) => {
  const state = getState();
  const intervalId = state.brands.pollingIntervals[brandUrl];
  
  // Clear the interval
  if (intervalId) {
    clearInterval(intervalId);
    dispatch(clearPollingInterval({ brandUrl }));
  }
  
  // Stop polling in state
  dispatch(stopPolling({ brandUrl }));
};

// Selector functions for easier state access
export const selectBrands = (state) => state.brands.brands;
export const selectBrandsLoading = (state) => state.brands.loading;
export const selectBrandsError = (state) => state.brands.error;
export const selectCreateLoading = (state) => state.brands.createLoading;
export const selectCreateError = (state) => state.brands.createError;
export const selectCreateSuccess = (state) => state.brands.createSuccess;
export const selectProcessingBrands = (state) => state.brands.processingBrands;
export const selectIsPolling = (state) => state.brands.isPolling;
export const selectShowProcessingPopup = (state) => state.brands.showProcessingPopup;
export const selectCurrentProcessingBrand = (state) => state.brands.currentProcessingBrand;

// Selector for getting processing status of a specific brand
export const selectBrandProcessingStatus = (brandUrl) => (state) => {
  return state.brands.processingBrands[brandUrl] || null;
};

// Selector for getting all active processing brands
export const selectActiveProcessingBrands = (state) => {
  return Object.entries(state.brands.processingBrands)
    .filter(([_, brand]) => brand.isActive)
    .reduce((acc, [brandUrl, brand]) => {
      acc[brandUrl] = brand;
      return acc;
    }, {});
};

export default brandsSlice.reducer;